export interface RWQuestion {
  id: number;
  passage: string;
  question: string;
  choices: string[];
  correctAnswer: number;
  explanation: string;
  subtopic: string;
}

export const standardEnglishQuestions: RWQuestion[] = [
  {
    id: 1,
    passage: "The committee ______ meeting every Tuesday to discuss the project's progress.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "have been",
      "has been",
      "are",
      "were"
    ],
    correctAnswer: 1,
    explanation: "'Has been' is correct because 'committee' is a collective noun treated as singular in American English. The present perfect continuous tense ('has been meeting') indicates an ongoing action that started in the past.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 2,
    passage: "Neither the students nor the teacher ______ aware of the schedule change until this morning.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "were",
      "was",
      "are",
      "have been"
    ],
    correctAnswer: 1,
    explanation: "'Was' is correct because with 'neither...nor' constructions, the verb agrees with the subject closest to it. Since 'teacher' (singular) is closest to the verb, we use the singular 'was.'",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 3,
    passage: "The collection of rare manuscripts ______ housed in the library's special archives.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "are",
      "is",
      "were",
      "have been"
    ],
    correctAnswer: 1,
    explanation: "'Is' is correct because the subject is 'collection' (singular), not 'manuscripts.' The prepositional phrase 'of rare manuscripts' modifies the subject but does not change its number.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 4,
    passage: "Each of the participants ______ required to submit a consent form before the study begins.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "are",
      "is",
      "were",
      "have been"
    ],
    correctAnswer: 1,
    explanation: "'Is' is correct because 'each' is always singular, regardless of the prepositional phrase that follows. 'Each' always takes a singular verb.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 5,
    passage: "The data from the recent surveys ______ that consumer preferences have shifted significantly.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "suggests",
      "suggest",
      "suggesting",
      "has suggested"
    ],
    correctAnswer: 1,
    explanation: "'Suggest' is correct because 'data' is the plural form of 'datum' and takes a plural verb in formal writing. The present tense indicates current findings.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 6,
    passage: "The scientist presented her findings to the review board, ______ had been eagerly awaiting the results.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "who",
      "which",
      "that",
      "whom"
    ],
    correctAnswer: 0,
    explanation: "'Who' is correct because it refers to people (the review board members) and serves as the subject of the relative clause. 'Which' is for things, and 'whom' is used for objects, not subjects.",
    subtopic: "Pronoun Usage"
  },
  {
    id: 7,
    passage: "Between you and ______, I think the proposal needs significant revision.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "I",
      "me",
      "myself",
      "mine"
    ],
    correctAnswer: 1,
    explanation: "'Me' is correct because pronouns following prepositions ('between') must be in the objective case. 'Between you and me' is the correct form, not 'between you and I.'",
    subtopic: "Pronoun Usage"
  },
  {
    id: 8,
    passage: "The team celebrated ______ victory with a dinner at the coach's favorite restaurant.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "its",
      "it's",
      "their",
      "there"
    ],
    correctAnswer: 0,
    explanation: "'Its' is correct because 'team' is a collective noun treated as singular, requiring the singular possessive pronoun 'its' (without an apostrophe). 'It's' means 'it is.'",
    subtopic: "Pronoun Usage"
  },
  {
    id: 9,
    passage: "The archaeologist discovered an ancient ______ that provided new insights into the civilization's religious practices.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "artifact, which",
      "artifact which",
      "artifact, that",
      "artifact that"
    ],
    correctAnswer: 0,
    explanation: "'Artifact, which' is correct because the relative clause is non-restrictive (it provides additional information rather than essential identification). Non-restrictive clauses use 'which' and require a comma.",
    subtopic: "Punctuation"
  },
  {
    id: 10,
    passage: "The conference will address three main topics ______ climate change mitigation strategies, renewable energy technologies, and sustainable urban planning.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "topics: climate",
      "topics; climate",
      "topics, climate",
      "topics climate"
    ],
    correctAnswer: 0,
    explanation: "A colon after 'topics' is correct because it introduces a list that explains or elaborates on what comes before. The colon signals that specific examples will follow.",
    subtopic: "Punctuation"
  },
  {
    id: 11,
    passage: "After reviewing the evidence, the jury reached ______ verdict in less than two hours.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "its",
      "it's",
      "their",
      "they're"
    ],
    correctAnswer: 0,
    explanation: "'Its' is correct because 'jury' is a collective noun treated as singular in this context. The possessive form 'its' (without apostrophe) shows the verdict belongs to the jury.",
    subtopic: "Pronoun Usage"
  },
  {
    id: 12,
    passage: "The research team ______ its findings at the international conference next month.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "will present",
      "will presents",
      "will presenting",
      "will have present"
    ],
    correctAnswer: 0,
    explanation: "'Will present' is correct. The future tense is formed with 'will' plus the base form of the verb. 'Will' is already a helping verb, so the main verb does not need an -s ending.",
    subtopic: "Verb Forms"
  },
  {
    id: 13,
    passage: "By the time the rescue team arrived, the hikers ______ stranded on the mountain for three days.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "were",
      "have been",
      "had been",
      "are"
    ],
    correctAnswer: 2,
    explanation: "'Had been' is correct because the past perfect tense is needed to show that one past action (being stranded) occurred before another past action (the team's arrival).",
    subtopic: "Verb Forms"
  },
  {
    id: 14,
    passage: "The company's CEO announced that profits ______ by 15% over the previous quarter.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "increased",
      "have increased",
      "had increased",
      "will increase"
    ],
    correctAnswer: 2,
    explanation: "'Had increased' is correct because the past perfect tense indicates that the increase happened before the announcement (also in the past). The increase occurred in the previous quarter, before the CEO's announcement.",
    subtopic: "Verb Forms"
  },
  {
    id: 15,
    passage: "The orchestra's performance was ______ moving that several audience members were brought to tears.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "so",
      "such",
      "very",
      "too"
    ],
    correctAnswer: 0,
    explanation: "'So' is correct because it is used with adjectives in 'so...that' constructions to show cause and effect. 'So moving that' indicates the degree of emotion caused the result (tears).",
    subtopic: "Modifier Usage"
  },
  {
    id: 16,
    passage: "The new policy will affect all employees, ______ those in remote locations.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "accept",
      "except",
      "expect",
      "excerpt"
    ],
    correctAnswer: 1,
    explanation: "'Except' is correct because it means 'excluding' or 'not including.' The policy affects all employees with the exception of those in remote locations. 'Accept' means to receive or agree to something.",
    subtopic: "Word Choice"
  },
  {
    id: 17,
    passage: "The historian's argument is ______ on extensive archival research and primary source analysis.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "based",
      "basis",
      "basing",
      "bases"
    ],
    correctAnswer: 0,
    explanation: "'Based' is correct in the passive construction 'is based on,' which means 'is founded on' or 'relies on.' This is the standard idiom for describing the foundation of an argument.",
    subtopic: "Word Choice"
  },
  {
    id: 18,
    passage: "The committee members could not agree on a solution; ______, they decided to table the discussion until the next meeting.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "therefore",
      "however",
      "moreover",
      "furthermore"
    ],
    correctAnswer: 0,
    explanation: "'Therefore' is correct because it shows that the decision to table the discussion was a logical result of their inability to agree. The semicolon properly connects two independent clauses with a transitional adverb.",
    subtopic: "Sentence Structure"
  },
  {
    id: 19,
    passage: "______ through the dense forest for hours, the hikers finally emerged onto a clearing with a breathtaking view of the valley below.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "Having trekked",
      "They trekked",
      "Trekked",
      "To trek"
    ],
    correctAnswer: 0,
    explanation: "'Having trekked' is correct because it creates a participial phrase that correctly modifies the subject 'the hikers.' The perfect participle 'having trekked' shows that the trekking was completed before they emerged.",
    subtopic: "Modifier Usage"
  },
  {
    id: 20,
    passage: "The professor, along with several graduate students, ______ presenting research at the symposium this weekend.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "are",
      "is",
      "were",
      "have been"
    ],
    correctAnswer: 1,
    explanation: "'Is' is correct because the subject is 'professor' (singular). The phrase 'along with several graduate students' is a parenthetical addition and does not change the subject's number. The verb must agree with 'professor.'",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 21,
    passage: "The author's new novel—a sweeping saga spanning three generations—______ already sold over a million copies worldwide.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "have",
      "has",
      "having",
      "had"
    ],
    correctAnswer: 1,
    explanation: "'Has' is correct because the subject is 'novel' (singular). The em-dash phrase 'a sweeping saga spanning three generations' is an appositive that does not change the subject. Present perfect 'has sold' indicates a recent completed action.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 22,
    passage: "The students submitted ______ final projects before the deadline, but the professor noticed that several were missing required citations.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "their",
      "there",
      "they're",
      "its"
    ],
    correctAnswer: 0,
    explanation: "'Their' is the correct possessive pronoun referring to 'students' (plural). 'There' indicates a place, 'they're' is a contraction of 'they are,' and 'its' is singular possessive.",
    subtopic: "Pronoun Usage"
  },
  {
    id: 23,
    passage: "The report, which was compiled by a team of independent auditors, ______ several significant discrepancies in the company's financial records.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "reveal",
      "reveals",
      "revealing",
      "have revealed"
    ],
    correctAnswer: 1,
    explanation: "'Reveals' is correct because the subject is 'report' (singular). The relative clause 'which was compiled by a team of independent auditors' is a non-restrictive modifier and does not change the subject's number.",
    subtopic: "Subject-Verb Agreement"
  },
  {
    id: 24,
    passage: "If the board ______ the merger proposal, the company would become the largest retailer in the region.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "approves",
      "approved",
      "will approve",
      "has approved"
    ],
    correctAnswer: 1,
    explanation: "'Approved' is correct because this is a conditional sentence using the subjunctive/past tense to describe a hypothetical situation. The structure 'If...approved...would become' is the standard form for unreal or hypothetical conditionals.",
    subtopic: "Verb Forms"
  },
  {
    id: 25,
    passage: "The senator's speech was both eloquent ______ persuasive, earning a standing ovation from the assembled delegates.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "or",
      "nor",
      "and",
      "but"
    ],
    correctAnswer: 2,
    explanation: "'And' is correct because the correlative conjunction 'both...and' requires 'and' as its second element. 'Both eloquent and persuasive' is the standard parallel construction.",
    subtopic: "Sentence Structure"
  },
  {
    id: 26,
    passage: "The children's museum features interactive exhibits ______ allow visitors of all ages to explore science, history, and art through hands-on activities.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "which",
      "who",
      "that",
      "whom"
    ],
    correctAnswer: 2,
    explanation: "'That' is correct because it introduces a restrictive relative clause that identifies which exhibits are being described. Restrictive clauses use 'that' (without a comma) when referring to things. 'Which' would require a comma and would make the clause non-restrictive.",
    subtopic: "Pronoun Usage"
  },
  {
    id: 27,
    passage: "The marathon runner, ______ had trained for over a year, crossed the finish line in record time despite the extreme heat.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "which",
      "whom",
      "who",
      "that"
    ],
    correctAnswer: 2,
    explanation: "'Who' is correct because it refers to a person (the marathon runner) and serves as the subject of the relative clause ('who had trained'). 'Whom' would be used if the pronoun were an object, not a subject.",
    subtopic: "Pronoun Usage"
  },
  {
    id: 28,
    passage: "The laboratory results were ______ surprising: not only did the new compound reduce inflammation, but it also appeared to accelerate tissue regeneration.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "doubly",
      "double",
      "doubled",
      "doubles"
    ],
    correctAnswer: 0,
    explanation: "'Doubly' is correct because an adverb is needed to modify the adjective 'surprising.' 'Doubly' means 'to twice the degree,' which fits the context of two unexpected findings. 'Double' is an adjective or verb, not an adverb.",
    subtopic: "Modifier Usage"
  },
  {
    id: 29,
    passage: "The city's infrastructure ______ significantly since the 1980s, with new bridges, expanded transit lines, and modernized water treatment facilities.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "improved",
      "has improved",
      "improves",
      "had improved"
    ],
    correctAnswer: 1,
    explanation: "'Has improved' is correct because the present perfect tense describes a change that began in the past and continues to be relevant in the present. The phrase 'since the 1980s' is a key signal that the present perfect is needed.",
    subtopic: "Verb Forms"
  },
  {
    id: 30,
    passage: "The award was presented to ______ the committee considered the most deserving candidate: a young researcher whose work on antibiotic resistance had already saved thousands of lives.",
    question: "Which choice completes the text so that it conforms to the conventions of Standard English?",
    choices: [
      "who",
      "whoever",
      "whom",
      "whomever"
    ],
    correctAnswer: 2,
    explanation: "'Whom' is correct because it is the object of the preposition 'to' and the object of the verb 'considered' within the relative clause. The test: 'the committee considered him/her the most deserving candidate' — the objective pronoun 'him/her' confirms 'whom' is correct.",
    subtopic: "Pronoun Usage"
  }
];
